import { useEffect } from 'react';
import type { NextRouter } from 'next/router';
import scrollToElementById from '@utils/scrollToElementById';
import { logScrollTrigger } from '@utils/scrollDebug';

/** Hashes handled only by modals in Layout — do not scroll the page. */
const MODAL_HASH_SUBSTRINGS = ['designStor', 'interiorDesing'];
const shouldScrollToElementForHash = (hash: string): boolean => {
  if (!hash || hash.length <= 1) return false;
  const lower = hash.toLowerCase();
  return !MODAL_HASH_SUBSTRINGS.some(s => lower.includes(s.toLowerCase()));
};
const scrollToCurrentUrlHash = (): void => {
  if (typeof window === 'undefined') return;
  const {
    hash
  } = window.location;
  if (!shouldScrollToElementForHash(hash)) return;
  const id = decodeURIComponent(hash.slice(1)).trim();
  if (!id) return;
  scrollToElementById(id);
};

/**
 * After client-side navigation Next.js scrolls to top by default, which overrides
 * the browser fragment scroll. Re-apply scroll to #id shortly after routeChangeComplete.
 */
const useHashScrollAfterRoute = (router: NextRouter): void => {
  useEffect(() => {
    const schedule = () => {
      // Next scrolls to top on routeChangeComplete; wait a tick so the target node exists after paint.
      window.setTimeout(() => {
        logScrollTrigger('useHashScrollAfterRoute — scrollToCurrentUrlHash', {
          hash: window.location.hash
        });
        scrollToCurrentUrlHash();
      }, 50);
    };
    router.events.on('routeChangeComplete', schedule);
    schedule();
    return () => {
      router.events.off('routeChangeComplete', schedule);
    };
  }, [router]);
};
export default useHashScrollAfterRoute;